import { InFilterDefinition } from '@factorialco/f0-react'
import {
  EmployeeEmployeesFilterDocument,
  EmployeeEmployeesFilterQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'

const extractConnection = (data: EmployeeEmployeesFilterQuery) =>
  data.employees.employeeNamesConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = NonNullable<ReturnType<typeof extractNodes>>[number]

type Params = {
  label: string
}

export const useEmployeesFilter = ({ label }: Params): InFilterDefinition<number> => {
  const { company } = useCurrentSession()

  const companyId = company.id

  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: EmployeeEmployeesFilterDocument,
      perPage: 25,
      mapData: (data) => {
        const connection = extractConnection(data)
        return extractNodes(connection)
      },
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search,
        companyId,
      }),
    },
    [companyId]
  )

  return {
    label,
    type: 'in' as const,
    options: {
      source,
      mapOptions: (employee: Node) => ({
        label: employee.fullName,
        value: employee.id,
      }),
    },
  }
}
